import React, { useRef, useEffect, useState, useCallback, ChangeEvent } from 'react';
import { IHeaderParams, RowNode } from 'ag-grid-community';

interface SelectAllHeaderProps extends IHeaderParams {
    onSelectAllCurrentPage: () => void;
    onDeselectAll: () => void;
}

const SelectAllHeader: React.FC<SelectAllHeaderProps> = ({
    displayName,
    onSelectAllCurrentPage,
    onDeselectAll,
    api,
}) => {
    const checkboxRef = useRef<HTMLInputElement>(null);
    const [isAllCurrentPageSelected, setIsAllCurrentPageSelected] = useState(false);
    const [hasPartialSelection, setHasPartialSelection] = useState(false);

    const calculateSelectionState = useCallback(() => {
        if (!api) return;

        try {
            const displayedRowCount = api.getDisplayedRowCount();
            if (displayedRowCount === 0) {
                setIsAllCurrentPageSelected(false);
                setHasPartialSelection(false);
                return;
            }

            // For server-side model, use forEachNode with displayed check
            let visibleNodeCount = 0;
            let selectedVisibleCount = 0;

            api.forEachNode((rowNode: RowNode) => {
                if (rowNode.displayed) {
                    visibleNodeCount++;
                    if (rowNode.isSelected()) {
                        selectedVisibleCount++;
                    }
                }
            });

            const allSelected = visibleNodeCount > 0 && selectedVisibleCount === visibleNodeCount;
            const partialSelected = selectedVisibleCount > 0 && !allSelected;

            setIsAllCurrentPageSelected(allSelected);
            setHasPartialSelection(partialSelected);
        } catch (error) {
            // Fallback if methods are not available
            setIsAllCurrentPageSelected(false);
            setHasPartialSelection(false);
        }
    }, [api]);

    useEffect(() => {
        calculateSelectionState();

        if (api) {
            const handleSelectionChanged = () => {
                // Small delay to ensure selection state is updated
                setTimeout(() => {
                    calculateSelectionState();
                }, 10);
            };

            const handleFilterChanged = () => {
                calculateSelectionState();
            };

            api.addEventListener('selectionChanged', handleSelectionChanged);
            api.addEventListener('filterChanged', handleFilterChanged);

            return () => {
                api.removeEventListener('selectionChanged', handleSelectionChanged);
                api.removeEventListener('filterChanged', handleFilterChanged);
            };
        }
    }, [api, calculateSelectionState]);

    useEffect(() => {
        if (checkboxRef.current) {
            if (isAllCurrentPageSelected) {
                checkboxRef.current.checked = true;
                checkboxRef.current.indeterminate = false;
            } else if (hasPartialSelection) {
                checkboxRef.current.checked = false;
                checkboxRef.current.indeterminate = true;
            } else {
                checkboxRef.current.checked = false;
                checkboxRef.current.indeterminate = false;
            }
        }
    }, [isAllCurrentPageSelected, hasPartialSelection]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            onSelectAllCurrentPage();
        } else {
            onDeselectAll();
        }
    };

    return (
        <div className="d-flex align-items-center" style={{ gap: '4px' }}>
            <input
                ref={checkboxRef}
                type="checkbox"
                onChange={handleChange}
                className="ag-checkbox-input"
                style={{ 
                    cursor: 'pointer',
                    width: '16px',
                    height: '16px',
                    margin: '0'
                }}
            />
            <span className="ag-header-cell-text">
                {displayName}
            </span>
        </div>
    );
};

export default SelectAllHeader;
